import { BlockInstanceType, LinkType } from 'application-types';

// LinkType with documentId (added at runtime for Document links)
type LinkTypeWithDocumentId = LinkType & {
  documentId?: string;
};

/**
 * Constructs a link object with the refreshed URI, preserving all original properties
 * including runtime properties like documentId
 */
function constructLinkWithRefreshedUri(
  link: LinkTypeWithDocumentId,
  refreshedUri: string | { en: string }
): LinkTypeWithDocumentId {
  return {
    ...link,
    uri: refreshedUri,
  };
}

/**
 * Refreshes all URLs in a multi-language URI object in parallel
 * Preserves the structure and ensures 'en' property exists
 */
async function refreshMultiLanguageUri(
  originalUri: { en: string },
  documentId: string,
  refreshIfNeeded: (mediaId: string, currentUrl: string) => Promise<string>
): Promise<{ en: string }> {
  const entries = Object.entries(originalUri).filter(
    ([, url]) => typeof url === 'string'
  );

  const refreshedEntries = await Promise.all(
    entries.map(async ([langCode, url]) => [
      langCode,
      await refreshIfNeeded(documentId, url),
    ])
  );

  const refreshedUri: { en: string } & Record<string, string> = { en: '' };

  for (const [langCode, url] of refreshedEntries) {
    refreshedUri[langCode] = url;
  }

  return refreshedUri;
}

/**
 * Processes a single block's link, refreshing the URL if needed
 * Preserves the original URI structure (string or multi-language object)
 */
async function processBlockLink(
  link: LinkType | undefined,
  refreshIfNeeded: (mediaId: string, currentUrl: string) => Promise<string>
): Promise<LinkType | undefined> {
  if (!link) return link;

  const linkWithDocumentId: LinkTypeWithDocumentId = link;
  const documentId = linkWithDocumentId.documentId;

  if (!documentId) return link;

  // Handle string URI
  if (typeof link.uri === 'string') {
    const refreshedUrl = await refreshIfNeeded(documentId, link.uri);

    return constructLinkWithRefreshedUri(link, refreshedUrl);
  }

  // Handle multi-language object URI
  // The type definition says { en: string }, but runtime may have other language codes
  if (typeof link.uri === 'object' && link.uri !== null) {
    const preservedUri = await refreshMultiLanguageUri(
      link.uri,
      documentId,
      refreshIfNeeded
    );

    return constructLinkWithRefreshedUri(link, preservedUri);
  }

  // If URI is undefined or doesn't match expected types, return original
  return link;
}

/**
 * Processes a block and all its nested children, refreshing signed URLs for PDF attachments.
 * This function recursively traverses the block structure and updates
 * any links with documentId to use refreshed URLs.
 *
 * @param block - The block instance to process (can be null or undefined)
 * @param refreshIfNeeded - Function to refresh a URL if needed, given mediaId and currentUrl
 * @returns The processed block with refreshed URLs, or null/undefined if input was null/undefined
 */
export async function processBlockContentWithUrlRefresh(
  block: BlockInstanceType | null | undefined,
  refreshIfNeeded: (mediaId: string, currentUrl: string) => Promise<string>
): Promise<BlockInstanceType | null | undefined> {
  if (!block) return block;

  const processedBlockItem = { ...block };

  processedBlockItem.link = await processBlockLink(
    processedBlockItem.link,
    refreshIfNeeded
  );

  // Process root-level children (BlockInstanceType has both root-level children and properties.children)
  if (
    processedBlockItem.children &&
    Array.isArray(processedBlockItem.children)
  ) {
    const processedRootChildren = await Promise.all(
      processedBlockItem.children.map(childBlock =>
        processBlockContentWithUrlRefresh(childBlock, refreshIfNeeded)
      )
    );

    processedBlockItem.children = processedRootChildren.filter(
      (child): child is BlockInstanceType =>
        child !== null && child !== undefined
    );
  }

  // Process nested blocks in properties.children recursively
  if (processedBlockItem.properties?.children) {
    const children = processedBlockItem.properties.children;

    // Only process if children is an array of BlockInstanceType
    if (Array.isArray(children)) {
      // Process all children in parallel for better performance
      const processedChildren = await Promise.all(
        children.map(nestedBlockItem =>
          processBlockContentWithUrlRefresh(nestedBlockItem, refreshIfNeeded)
        )
      );

      // Create a new properties object with processed children, filtering out null/undefined
      processedBlockItem.properties = {
        ...processedBlockItem.properties,
        children: processedChildren.filter(
          (child): child is BlockInstanceType =>
            child !== null && child !== undefined
        ),
      };
    }
  }

  return processedBlockItem;
}
